/**
 * Wenn die Leiste zu voll wird: das Unwichtigste zuerst ins Menü.
 *
 * DAS PROBLEM. Die Editor-Leiste stand in einer Reihe und war auf
 * `overflow-x-auto` gesetzt. Wurde das Fenster schmaler, schob sich das Ende
 * aus dem Bild — ausgerechnet „Auf Live", und nichts wies darauf hin, dass es
 * noch da war. David am 19.08.2026: „wenn das fenster kleiner schiebt sich
 * alles raus. wir brauchen hier generell ein anderes system."
 *
 * DIE REGEL DER GROSSEN. SAP Fiori sortiert die Knöpfe einer Leiste nach
 * Wichtigkeit und lässt sie von rechts nach links in den Überlauf wandern,
 * damit die wichtigsten zuletzt gehen; jede Handlung muss dabei jederzeit
 * erreichbar bleiben. PatternFly setzt die Obergrenze noch schärfer: „no more
 * than 2 buttons" in der Leiste, der Rest ins Überlauf-Menü. Brad Frost nennt
 * das Muster Priority+ und hält ihm zugute, dass es eine Spur legt, der man
 * folgen kann — anders als ein Symbol, hinter dem alles verschwindet.
 *
 * WIE HIER GEMESSEN WIRD. Nicht geraten, sondern gefragt: Passt der Inhalt
 * noch in die Leiste? Wenn nein, fällt die nächste Stufe weg, und es wird
 * erneut gefragt. Feste Bildschirmbreiten wären eine Annahme über den Inhalt,
 * und die kippt beim ersten langen Projektnamen.
 *
 * Gerechnet wird bei jeder Prüfung von vorn. Das kostet ein paar Messungen,
 * spart aber die Rückwärts-Logik: Wird das Fenster wieder breit, kommt alles
 * von selbst zurück, ohne dass zwei Regeln gegeneinander arbeiten.
 */
import { nextTick, ref, type Ref } from 'vue'

export interface LeistenPlatz {
  /** Wie viele Stufen gerade eingeklappt sind. 0 = alles ist zu sehen. */
  stufe: Ref<number>
  /** Ist das Element dieser Stufe noch in der Leiste? */
  zeigt: (stufe: number) => boolean
  /** Neu messen. Nach jeder Größen- oder Inhaltsänderung. */
  pruefe: () => Promise<void>
}

export function useLeistenPlatz(stufen: number, ueberfuellt: () => boolean): LeistenPlatz {
  const stufe = ref(0)
  let laeuft = false

  async function pruefe(): Promise<void> {
    // Zwei Läufe gleichzeitig würden sich gegenseitig die Stufe verstellen.
    if (laeuft) return
    laeuft = true
    try {
      stufe.value = 0
      await nextTick()
      while (stufe.value < stufen && ueberfuellt()) {
        stufe.value += 1
        await nextTick()
      }
    } finally {
      laeuft = false
    }
  }

  return {
    stufe,
    zeigt: (ab: number) => stufe.value < ab,
    pruefe,
  }
}
